import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { actionVentasGet } from "../../redux/actions/ventas/ventas";

import { Button, Card, Input, Space, Typography, Row, Col, Skeleton, ConfigProvider } from "antd";
import { SearchOutlined } from "@ant-design/icons";

import { showHerramientasEffect } from "../../components/utils/antWaveEffect";
import "./PaquetesPage.css";

const { Title, Text, Paragraph } = Typography;

const BANNER_BG = "linear-gradient(135deg, #3d0011 0%, #5c0019 100%)";

const money = (n) =>
  n == null ? "—" : `$${parseFloat(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function VentasPage() {
  const dispatch = useDispatch();
  const { items = [], loading } = useSelector((state) => state.ventas);
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [activeFilter, setActiveFilter] = useState("todos");

  const lastFetchKey = useRef("");

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchParams = useMemo(() => ({ search: searchDebounced || undefined }), [searchDebounced]);

  useEffect(() => {
    const key = JSON.stringify(fetchParams);
    if (lastFetchKey.current === key) return;
    lastFetchKey.current = key;
    dispatch(actionVentasGet(fetchParams));
  }, [dispatch, fetchParams]);

  const counts = useMemo(() => ({
    total: items.length,
    conSaldo: items.filter((v) => Boolean(v.es_credito) && parseFloat(v.saldo_pendiente || 0) > 0).length,
    canceladas: items.filter((v) => Boolean(v.cancelada)).length,
  }), [items]);

  const filteredItems = useMemo(() => {
    if (activeFilter === "con_saldo") return items.filter((v) => Boolean(v.es_credito) && parseFloat(v.saldo_pendiente || 0) > 0);
    if (activeFilter === "canceladas") return items.filter((v) => Boolean(v.cancelada));
    return items;
  }, [items, activeFilter]);

  const STAT_CARDS = [
    { key: "todos", label: "Total de ventas", icon: <i className="fa-solid fa-receipt" />, iconCls: "paq-stat-icon-sonido", count: counts.total },
    { key: "con_saldo", label: "A credito con saldo", icon: <i className="fa-solid fa-money-check-dollar" />, iconCls: "paq-stat-icon-barra", count: counts.conSaldo },
    { key: "canceladas", label: "Canceladas", icon: <i className="fa-solid fa-ban" />, iconCls: "paq-stat-icon-receta", count: counts.canceladas },
  ];

  return (
    <ConfigProvider wave={{ showEffect: showHerramientasEffect }}>
      <main className="paq-main">
        <div className="paq-content">
          <section className="paq-section">
            <div className="paq-filters-panel">
              <Space direction="vertical" size={2} style={{ marginBottom: 16 }}>
                <Title level={2} className="paq-title">Ventas</Title>
                <Text className="paq-subtitle">Historial de ventas a clientes.</Text>
              </Space>

              <Row gutter={[16, 14]}>
                <Col xs={24}>
                  <div className="paq-field-label">Buscador</div>
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar por cliente o folio de venta..."
                    suffix={<SearchOutlined className="paq-input-suffix" />}
                    className="paq-control"
                    allowClear
                  />
                </Col>
              </Row>
            </div>

            <div className="paq-stats-row" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
              {STAT_CARDS.map(({ key, label, icon, iconCls, count }) => (
                <Card
                  key={key}
                  className={`paq-stat-card ${activeFilter === key ? "paq-stat-active" : ""}`}
                  hoverable
                  onClick={() => setActiveFilter((p) => (p === key ? "todos" : key))}
                >
                  <Space align="center" size={10}>
                    <div className={`paq-stat-icon ${iconCls}`}>{icon}</div>
                    <div>
                      <div className="paq-stat-value">{count}</div>
                      <div className="paq-stat-label">{label}</div>
                    </div>
                  </Space>
                </Card>
              ))}
            </div>

            <div className="paq-expedientes-card">
              <div className="paq-toolbar">
                <div className="paq-toolbar-left">
                  <Title level={4} style={{ marginBottom: 0 }}>Ventas ({filteredItems.length})</Title>
                  <Text type="secondary">{filteredItems.length} ventas encontradas</Text>
                </div>
                <div className="paq-toolbar-right">
                  <>
                    <Button
                      type="primary"
                      icon={<i className="fa-solid fa-plus" />}
                      onClick={() => navigate("/app/ventas/crear")}
                      style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}
                    >
                      Nueva venta
                    </Button>
                  </>
                </div>
              </div>

              {!loading && filteredItems.length === 0 && (
                <div className="paq-empty">
                  <div style={{ fontSize: 16, fontWeight: 500 }}>Sin ventas que coincidan con los filtros</div>
                </div>
              )}

              <div className="paq-category-block">
                <div className="paq-grid">
                  {loading && Array.from({ length: 4 }).map((_, i) => (
                    <div key={`sk-${i}`} className="paq-card paq-card-skeleton">
                      <div className="paq-card-header">
                        <Skeleton active title={{ width: "60%" }} paragraph={{ rows: 1, width: "40%" }} />
                      </div>
                      <div className="paq-card-body">
                        <Skeleton active title={false} paragraph={{ rows: 3 }} />
                      </div>
                    </div>
                  ))}

                  {!loading && filteredItems.length > 0 && (
                    <>
                      {filteredItems.map((v) => (
                        <div key={v.id_venta} className="paq-card">
                          <div className="paq-card-banner" style={{ background: BANNER_BG }}>
                            <i className="fa-solid fa-receipt paq-card-banner-icon" />
                          </div>

                          <div className="paq-card-header">
                            <div className="paq-card-head">
                              <Paragraph className="paq-card-name" ellipsis={{ rows: 2 }}>
                                {v.cliente_nombre || "Publico general"}
                              </Paragraph>
                              <span className="paq-tipo-badge paq-tipo-sonido">{money(v.total)}</span>
                            </div>
                          </div>

                          <div className="paq-card-body">
                            <div className="paq-contenidos-box">
                              <i className="fa-solid fa-hashtag paq-info-icon" />
                              <div className="paq-info-text">
                                <span className="paq-info-label">Venta</span>
                                <span className="paq-info-value">#{v.id_venta}</span>
                              </div>
                            </div>
                            {Boolean(v.es_credito) && (
                              <div className="paq-contenidos-box">
                                <i className="fa-solid fa-money-check-dollar paq-info-icon paq-info-icon-receta" />
                                <div className="paq-info-text">
                                  <span className="paq-info-label paq-info-label-receta">Saldo pendiente</span>
                                  <span className="paq-info-value">{money(v.saldo_pendiente)}</span>
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="paq-card-footer">
                            {v.cancelada ? (
                              <div className="paq-bottom-strip is-red">
                                <span className="paq-strip-label">Cancelada</span>
                              </div>
                            ) : v.es_credito ? (
                              <div className={`paq-bottom-strip ${parseFloat(v.saldo_pendiente || 0) > 0 ? "is-red" : "is-green"}`}>
                                <span className="paq-strip-label">{parseFloat(v.saldo_pendiente || 0) > 0 ? "Credito pendiente" : "Credito liquidado"}</span>
                              </div>
                            ) : (
                              <div className="paq-bottom-strip is-green">
                                <span className="paq-strip-label">Pagada</span>
                              </div>
                            )}
                            <Button
                              className="paq-btn-details"
                              icon={<i className="fa-solid fa-circle-info" />}
                              onClick={() => navigate(`/app/ventas/${v.id_venta}`)}
                            >
                              Ver detalles
                            </Button>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </ConfigProvider>
  );
}
